import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  Send,
  Heart
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { organizationInfo } from '../../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
      console.error('Newsletter subscription error:', error);
    } finally {
      setSubscribing(false);
    }
  };

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Our Impact', path: '/impact' },
    { name: 'Get Involved', path: '/get-involved' },
    { name: 'Thank You', path: '/thank-you' },
    { name: 'Store', path: '/store' },
    { name: 'Donate', path: '/donate' },
  ];

  const socialLinks = [
    { icon: Facebook, href: organizationInfo.socialMedia.facebook, label: 'Facebook' },
    { icon: Instagram, href: organizationInfo.socialMedia.instagram, label: 'Instagram' },
    { icon: Linkedin, href: organizationInfo.socialMedia.linkedin, label: 'LinkedIn' },
    { icon: Youtube, href: organizationInfo.socialMedia.youtube, label: 'YouTube' },
  ];

  return (
    <footer className="bg-white border-t">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_mentorsinmotion/artifacts/wv1omvza_MentorsInMotion%20-%20Logo.png"
                alt={organizationInfo.name}
                className="h-12 w-auto"
              />
              <span className="font-bold text-lg text-gray-900">
                Mentors in Motion
              </span>
            </Link>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Empowering youth in underserved communities through mentoring, tutoring, and creative learning programs since 2009.
            </p>
            <div className="space-y-3">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(organizationInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-gray-600 hover:text-red-600 transition-colors text-sm"
              >
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600" />
                <span>{organizationInfo.address}</span>
              </a>
              <a
                href={`tel:${organizationInfo.phone}`}
                className="flex items-center gap-3 text-gray-600 hover:text-red-600 transition-colors text-sm"
              >
                <Phone className="w-5 h-5 flex-shrink-0 text-red-600" />
                <span>{organizationInfo.phone}</span>
              </a>
              <a
                href={`mailto:${organizationInfo.email}`}
                className="flex items-center gap-3 text-gray-600 hover:text-red-600 transition-colors text-sm"
              >
                <Mail className="w-5 h-5 flex-shrink-0 text-red-600" />
                <span>{organizationInfo.email}</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg text-gray-900 mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-red-600 transition-colors text-sm inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-semibold text-lg text-gray-900 mb-6">Stay Updated</h3>
            <p className="text-gray-600 text-sm mb-4">
              Join our mailing list to receive news on our programs and impact.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 pr-12 focus:border-red-500 focus:ring-red-500"
                  required
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
            
            {/* Social Links */}
            <div className="mt-8">
              <h4 className="font-medium text-sm mb-4 text-gray-700">Follow Us</h4>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <button
                    key={social.label}
                    type="button"
                    className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 hover:bg-red-600 hover:text-white transition-all duration-200 cursor-pointer"
                    aria-label={social.label}
                    onClick={() => {
                      const newWindow = window.open('about:blank', '_blank');
                      if (newWindow) {
                        newWindow.location.href = social.href;
                      } else {
                        window.location.href = social.href;
                      }
                    }}
                  >
                    <social.icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Donate CTA */}
          <div>
            <h3 className="font-semibold text-lg text-gray-900 mb-6">Support Our Mission</h3>
            <p className="text-gray-600 text-sm mb-6">
              Your generosity can change a child's trajectory. Join us in developing scholars that build thriving communities.
            </p>
            <Link to="/donate">
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold">
                <Heart className="w-4 h-4 mr-2" />
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-500 text-sm text-center md:text-left">
              <p>
                Mentors in Motion is a registered 501(c)(3) nonprofit organization.
              </p>
              <p className="mt-1">© {new Date().getFullYear()} Mentors in Motion. All Rights Reserved.</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-3">
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <Link to="/privacy" className="text-gray-500 hover:text-red-600 transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-gray-500 hover:text-red-600 transition-colors">
                  Terms of Use
                </Link>
                <Link to="/accessibility" className="text-gray-500 hover:text-red-600 transition-colors">
                  Accessibility
                </Link>
              </div>
              {/* Created by Sketched Impressions */}
              <a 
                href="https://sketchedimpressions.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-sm">Created by</span>
                <img 
                  src="https://customer-assets.emergentagent.com/job_youth-impact-3/artifacts/a9fzsiso_Sketched%20Impressions%20Logo.png" 
                  alt="Sketched Impressions" 
                  className="h-28 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
